import React from "react";
import { Alert, Box, Button, IconButton, LinearProgress, Stack, Typography } from "@mui/material";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import UploadRoundedIcon from "@mui/icons-material/UploadRounded";
import { formatImageSize } from "../../../utils/productImageUpload";
import { PRODUCT_IMAGE_MAX_SOURCE_BYTES } from "../../../utils/imageUpload";

const BRAND = { orange: "#F35A3A" };

const REMOVE_IMAGE_BUTTON_SX = {
  position: "absolute",
  top: -8,
  right: -8,
  width: 44,
  height: 44,
  p: 0,
  bgcolor: "transparent",
  zIndex: 1,
  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    right: 0,
    width: 24,
    height: 24,
    borderRadius: "50%",
    bgcolor: "var(--wp-panel-surface-elevated, white)",
    boxShadow: 1,
  },
  "&:hover": { bgcolor: "transparent" },
  "&:hover::before": { bgcolor: "var(--wp-panel-surface-soft, #F7F8FB)" },
  "&.Mui-focusVisible": {
    outline: `2px solid ${BRAND.orange}`,
    outlineOffset: 1,
  },
};

export default function ItemImagesEditor({
  existingImages = [],
  selectedFiles = [],
  previewUrls = [],
  processingImages = false,
  saving = false,
  imagePreparationProgress = null,
  imageUploadProgress = null,
  imageError = "",
  maxImages = 8,
  mobile = false,
  onRemoveExisting,
  onRemoveSelected,
  onFilesChange,
}) {
  return (
    <Box>
      {existingImages.length > 0 ? (
        <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mb: 1.5 }}>
          {existingImages.map((img) => (
            <Box key={img.identifier} sx={{ position: "relative", mb: 1 }}>
              <Box component="img" src={img.url} alt="Imagen actual" sx={{ width: 76, height: 76, objectFit: "cover", borderRadius: 2, border: "1px solid rgba(var(--wp-panel-ink-rgb, 25,36,47),0.1)" }} />
              <IconButton size="small" onClick={() => onRemoveExisting(img.identifier)} aria-label="Quitar imagen actual" sx={REMOVE_IMAGE_BUTTON_SX}>
                <CloseRoundedIcon sx={{ position: "absolute", top: 5, right: 5, fontSize: 14 }} />
              </IconButton>
            </Box>
          ))}
        </Stack>
      ) : null}

      {previewUrls.length > 0 ? (
        <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mb: 1.5 }}>
          {previewUrls.map((url, index) => (
            <Box key={url} sx={{ position: "relative", mb: 1 }}>
              <Box component="img" src={url} alt={`Nueva imagen ${index + 1}`} sx={{ width: 76, height: 76, objectFit: "cover", borderRadius: 2, border: `1px solid ${BRAND.orange}` }} />
              <Typography variant="caption" sx={{ display: "block", maxWidth: 76, textAlign: "center" }}>{formatImageSize(selectedFiles[index]?.size)}</Typography>
              <IconButton size="small" onClick={() => onRemoveSelected(index)} aria-label={`Quitar nueva imagen ${index + 1}`} sx={REMOVE_IMAGE_BUTTON_SX}>
                <CloseRoundedIcon sx={{ position: "absolute", top: 5, right: 5, fontSize: 14 }} />
              </IconButton>
            </Box>
          ))}
        </Stack>
      ) : null}

      {processingImages ? (
        <Box sx={{ mb: 1.5 }}>
          <LinearProgress variant={Number.isFinite(imagePreparationProgress) ? "determinate" : "indeterminate"} value={Number.isFinite(imagePreparationProgress) ? imagePreparationProgress : undefined} aria-label="Progreso de preparación de imágenes" />
          <Typography variant="caption" color="text.secondary">Optimizando imágenes antes de subir{Number.isFinite(imagePreparationProgress) ? `… ${imagePreparationProgress}%` : "…"}</Typography>
        </Box>
      ) : null}

      {saving && selectedFiles.length ? (
        <Box sx={{ mb: 1.5 }}>
          <LinearProgress variant={Number.isFinite(imageUploadProgress) ? "determinate" : "indeterminate"} value={Number.isFinite(imageUploadProgress) ? imageUploadProgress : undefined} aria-label="Progreso de carga de imágenes" />
          <Typography variant="caption" color="text.secondary">{imageUploadProgress === 100 ? "Imágenes enviadas. El servidor está terminando de procesarlas…" : `Subiendo imágenes${Number.isFinite(imageUploadProgress) ? `… ${imageUploadProgress}%` : "…"}`}</Typography>
        </Box>
      ) : null}

      {imageError ? <Alert severity="warning" sx={{ mb: 1.5 }}>{imageError}</Alert> : null}

      <Button component="label" disabled={processingImages || existingImages.length + selectedFiles.length >= maxImages} startIcon={<UploadRoundedIcon />} sx={{ textTransform: "none", fontWeight: 850, color: "var(--wp-panel-text-primary, #19242F)", border: "1px dashed rgba(var(--wp-panel-ink-rgb, 25,36,47),0.3)", borderRadius: 2, px: 2, py: 1 }}>
        {existingImages.length || selectedFiles.length ? "Agregar más imágenes" : "Subir imágenes"}
        <input type="file" hidden multiple accept="image/jpeg,image/png,image/webp" onChange={onFilesChange} />
      </Button>
      <Typography variant="caption" sx={{ display: "block", mt: 0.7, color: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.68)" }}>
        {existingImages.length + selectedFiles.length}/{maxImages} imágenes · JPG, PNG o WebP{!mobile ? ` · hasta ${formatImageSize(PRODUCT_IMAGE_MAX_SOURCE_BYTES)} cada una. El panel las optimiza antes de enviarlas.` : ""}
      </Typography>
    </Box>
  );
}
